import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Alert, Col, Input, Row, Switch } from 'antd'
import { useTranslation } from 'react-i18next'

import { Button, PageHeader, Table } from 'src/components/Ant'
import { Id, User } from 'src/types'
import * as UsersService from 'src/services/api/users'
import history from 'src/config/routes/history'
import useDocumentTitle from 'src/hooks/use-document-title'

import { WrapContent } from './styles'

function Users(): JSX.Element {
  const isSubscribed = useRef(true)
  const refTimeout = useRef<NodeJS.Timeout>(null)
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<string>('')
  const [list, setList] = useState<User[]>([])
  const [savingId, setSavingId] = useState<Id>()
  const [totalResult, setTotalResult] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [filter, setFilter] = useState<string>('')

  useDocumentTitle('web.user.title')

  const getUsers = useCallback(
    async (_page = 1, _filter = '') => {
      if (!isSubscribed.current) {
        return
      }
      setIsLoading(true)
      setHasError('')

      const response = await UsersService.getAll({
        filter: _filter,
        page: _page,
        withInactive: true
      })

      if (!isSubscribed.current) {
        return
      }
      setIsLoading(false)
      if (response.status === 200) {
        setTotalResult(response.data.total_results)
        setList(response.data.users)
        return
      }
      const genericError = t('web.user.errors.on-search')
      if (response.status === 500 || !response.error) {
        setHasError(genericError)
        return
      }

      setHasError(response?.error || genericError)
    },
    [t]
  )

  useEffect(() => {
    return () => {
      isSubscribed.current = false
    }
  }, [])

  useEffect(() => {
    clearTimeout(refTimeout.current)
    refTimeout.current = setTimeout(() => {
      getUsers(1, filter)
    }, 500)
  }, [filter, getUsers])

  return (
    <>
      <PageHeader
        extra={[
          <Button
            key="common.actions.add"
            onClick={() => {
              history.push('/users/create')
            }}
            size="default"
            title="common.actions.add"
            type="primary"
          />
        ]}
        title="web.user.title"
      />
      <WrapContent>
        <Row gutter={24} style={{ marginTop: 12, marginBottom: 12 }}>
          <Col span={20}>
            {hasError && <Alert message={hasError} showIcon type="error" />}
          </Col>
          <Col span={4}>
            <Input
              onChange={e => setFilter(e.target.value)}
              placeholder={t('common.filters')}
              value={filter}
            />
          </Col>
        </Row>
        <Table
          className="table-striped"
          columns={[
            {
              dataIndex: 'first_name',
              title: 'web.user.common.first_name.title',
              render: (value: string, userItem) =>
                `${value} ${userItem.last_name}`
            },
            {
              dataIndex: 'code',
              title: 'web.user.common.code.title'
            },
            {
              dataIndex: 'organization.trade_name',
              title: 'web.user.common.organization'
            },
            {
              title: 'common.active',
              dataIndex: 'active',
              render: (value: boolean, userItem) => (
                <Switch
                  loading={savingId === userItem.user_id}
                  onClick={(_, e) => e.stopPropagation()}
                  checked={value}
                  onChange={async checked => {
                    setSavingId(userItem.user_id)
                    const response = await UsersService.save({
                      user: {
                        ...userItem,
                        active: checked
                      }
                    })

                    if (response.status === 200) {
                      setList(oldList =>
                        oldList.map(oldUser =>
                          oldUser.user_id !== userItem.user_id
                            ? oldUser
                            : { ...oldUser, active: response.data.active }
                        )
                      )
                      setHasError('')
                    } else {
                      const genericError = t('web.user.errors.on-edit')
                      setHasError(genericError)
                    }

                    setSavingId(null)
                  }}
                />
              )
            }
          ]}
          dataSource={list}
          loading={isLoading}
          onChange={page => {
            setCurrentPage(page.current)

            getUsers(page.current, filter)
          }}
          onRow={record => ({
            onClick: () => history.push(`/users/edit/${record.user_id}`)
          })}
          pagination={{
            current: currentPage,
            pageSize: UsersService.PAGE_SIZE,
            total: totalResult
          }}
        />
      </WrapContent>
    </>
  )
}

export default Users
